import { SwitchOffControl } from '@Studio/Dialogs/Wizards/Controls/SwitchOffControl';
import { BaseControl, IControlParameters } from '@Common/Controls/BaseControl';
import { ISetValueParam } from '@Common/Controls/CheckBox';
import { IMargins } from '@Common/Controls/Controls';
import { MulticastDelegate } from '@Common/Core/MulticastDelegate';

export interface IRadioParameters extends IControlParameters {
    getRadioButtonsFromOneGroup?: () => (Radio | SwitchOffControl)[];
    caption?: string;
    showControls?: boolean;
    blockElement?: boolean;
    checked?: boolean;
    margins?: IMargins;
    id?: any;
    inlineBlockElement?: boolean;
    multiline?: boolean;
    tag?: any;
}

const defaultParams: IRadioParameters = {
    css: null,
    className: '',
    width: 'auto',
    height: 'auto',

    getRadioButtonsFromOneGroup: _.constant([]),
    caption: '',
    multiline: false,
    showControls: true,
    blockElement: true,
    inlineBlockElement: false,
    checked: false,
    margins: {
        left: false,
        right: false,
        top: false,
        bottom: false
    },
    validator: undefined,
    id: null
};

export class Radio extends BaseControl<IRadioParameters> {
    public readonly onChange = MulticastDelegate.create<ICallback<boolean>>();
    public readonly onValidate = MulticastDelegate.create<ICallback<boolean>>();

    private readonly checkedClassName = 'on';
    private readonly iconSize = 24;
    private readonly label: JQuery<HTMLElement>;

    private current: boolean;

    constructor(param?: IRadioParameters) {
        super();
        this._param = _.defaultsDeep(param ?? {}, defaultParams);
        this.current = this._param.checked ?? false;
        this.tag(this._param.tag);
        this._root = this._param.blockElement ? $('<div class="radio"/>') : $('<span class="radio"/>');
        this._root.toggleClass('block', this._param.blockElement);
        if (this._param.inlineBlockElement) {
            this._root.css('display', 'inline-block');
        }

        this._root.on('click', this.onClick);
        this._root.on('deselect', this.onDeselect);

        if (this._param.id) {
            this._root.addClass(this._param.id);
        }
        this._root.applyMargins(this._param.margins);

        $('<span />').appendTo(this._root);
        this.label = $('<label/>').html(this._param.caption);
        this.label.appendTo(this._root);

        if (this._param.multiline) {
            this.label.css({
                whiteSpace: 'normal'
            });
        }

        this.value(this.current);
        this._applyDefaultParam();
    }

    public validate(): void {
        let state = true;
        if (_.isFunction(this._param.validator)) {
            state = this._param.validator(this.current);
        }
        this.onValidate(state);
    }

    public value(data?: boolean, setParam?: ISetValueParam): boolean {
        if (data === undefined) {
            return this.current;
        }

        const valueChanged = Boolean(this.current) !== Boolean(data);

        if (data) {
            for (const oneOfGroup of this.getOtherRadioInGroup()) {
                oneOfGroup?.value(false, setParam);
            }
            this._root.addClass(this.checkedClassName);
        } else {
            this._root.toggleClass('hidden', !this._param.showControls);
            this._root.removeClass(this.checkedClassName);
        }

        this.current = data;

        if (!setParam?.stopEvents && valueChanged) {
            this.onChange(data);
        }
    }

    public setError(msg: string): void {
        super.setError(msg);
        this._root.addClass('radio-invalid');
        this._root.attr('title', msg);
    }

    public removeError(): void {
        super.removeError();
        this._root.removeClass('radio-invalid');
        this._root.attr('title', '');
    }

    public adjust(width: number, height: number): void {
        super.adjust(width, height);

        if (!this._param.multiline) {
            let paddingTop;
            if (height) {
                paddingTop = (height - this.iconSize) / 2 + 'px';
            } else {
                paddingTop = (this._root.height() - this.iconSize) / 2 + 'px';
            }

            this._root.css({
                paddingTop: paddingTop
            });
        }

        if (_.isString(this._param.width) && this._param.width.toLowerCase() === 'auto') {
            this.label.width('auto');
        } else {
            this.label.outerWidth(this._root.width() - this.iconSize);
        }
    }

    public readonly(data?: boolean): boolean {
        if (_.isBoolean(data)) {
            this._root.toggleClass('readonly-radio', data);
        }
        return super.readonly(data);
    }

    private readonly onClick = () => {
        const isAvailable = this._enabled && !this._readonly;
        if (isAvailable && this.current === false) {
            this.value(!this.current);
            this.validate();
        }
    };

    private readonly onDeselect = () => {
        this.validate();
        this.onChange(this._root.hasClass(this.checkedClassName));
    };

    private getOtherRadioInGroup(): (Radio | SwitchOffControl)[] {
        const group = this._param.getRadioButtonsFromOneGroup?.() ?? [];
        return group.filter(r => r !== this);
    }
}
